import React, { useRef, useMemo, useCallback } from "react";
import {
  GoogleMap,
  Marker,
  StandaloneSearchBox,
  useJsApiLoader,
} from "@react-google-maps/api";
import {
  TextField,
  InputAdornment,
  Box,
  CircularProgress,
} from "@mui/material";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";

const LIBRARIES = ["places"];
/**
 * @typedef {Object} LocationData
 * @property {number} lat - Latitud de la ubicación.
 * @property {number} lng - Longitud de la ubicación.
 * @property {string} address - Dirección formateada.
 */

/**
 * GoogleMapPicker - Componente reutilizable para selección de direcciones en un mapa.
 * * @param {Object} props
 * @param {string} props.address - Valor del campo de texto (dirección).
 * @param {number|string} props.lat - Coordenada de latitud.
 * @param {number|string} props.lng - Coordenada de longitud.
 * @param {Function} props.onLocationChange - Callback que retorna un objeto {lat, lng, address}.
 * @param {string} [props.height="300px"] - Altura del contenedor del mapa.
 */
const GoogleMapPicker = ({
  address,
  lat,
  lng,
  onLocationChange,
  height = "250px",
}) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_Google_maps_key,
    libraries: LIBRARIES,
  });

  const mapRef = useRef(null);
  const searchBoxRef = useRef(null);

  const center = useMemo(
    () => ({
      lat: Number(lat) || -34.6037, // Default por si no hay lat
      lng: Number(lng) || -58.3816,
    }),
    [lat, lng]
  );

  // --- Lógica de Geocodificación Inversa ---
  const reverseGeocode = useCallback(
    (latitude, longitude) => {
      const geocoder = new window.google.maps.Geocoder();
      const latlng = { lat: latitude, lng: longitude };

      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === "OK") {
          if (results[0]) {
            // Devolvemos la dirección encontrada al padre
            onLocationChange({
              lat: latitude,
              lng: longitude,
              address: results[0].formatted_address,
            });
          }
        } else {
          console.error("Geocoder falló debido a: " + status);
        }
      });
    },
    [onLocationChange]
  );

  const onPlacesChanged = () => {
    const places = searchBoxRef.current.getPlaces();
    if (places && places.length > 0) {
      const place = places[0];
      onLocationChange({
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        address: place.formatted_address,
      });
    }
  };

  const onMarkerDragEnd = (e) => {
    const newLat = e.latLng.lat();
    const newLng = e.latLng.lng();
    // Al terminar de arrastrar, buscamos la dirección de esas coordenadas
    reverseGeocode(newLat, newLng);
  };

  if (!isLoaded) return <CircularProgress />;

  return (
    <Box>
      <StandaloneSearchBox
        onLoad={(ref) => (searchBoxRef.current = ref)}
        onPlacesChanged={onPlacesChanged}
      >
        <TextField
          label="Dirección"
          fullWidth
          variant="standard"
          size="small"
          placeholder="Buscar dirección"
          value={address || ""}
          onChange={(e) =>
            onLocationChange({ lat, lng, address: e.target.value })
          }
          sx={{ mb: 1, backgroundColor: "#fff" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <TravelExploreIcon color="primary" />
              </InputAdornment>
            ),
          }}
        />
      </StandaloneSearchBox>

      <GoogleMap
        mapContainerStyle={{ height, width: "100%", borderRadius: "8px" }}
        center={center}
        zoom={lat ? 15 : 5}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        }}
      >
        <Marker position={center} draggable onDragEnd={onMarkerDragEnd} />
      </GoogleMap>
    </Box>
  );
};
/* ==========================================================================
  EJEMPLO DE USO:
  ==========================================================================

  import GoogleMapPicker from './components/GoogleMapPicker';

  function MiFormulario() {
    const [formData, setFormData] = useState({
      direccion: '',
      lat: 0,
      lng: 0
    });

    const handleLocationUpdate = (data) => {
      // data = { lat, lng, address }
      setFormData({
        ...formData,
        direccion: data.address,
        lat: data.lat,
        lng: data.lng
      });
    };

    return (
      <GoogleMapPicker 
        address={formData.direccion}
        lat={formData.lat}
        lng={formData.lng}
        onLocationChange={handleLocationUpdate}
      />
    );
  }
*/
export default GoogleMapPicker;
